import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ShippingPolicy: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith('zh') ? 'zh' : 'en';

  return (
    <main className="legal-page">
      <nav className="legal-nav">
        <Link to="/" className="legal-brand">{t('home.brand')}</Link>
        <Link to="/" className="legal-back">← {t('common.home')}</Link>
      </nav>

      <div className="legal-container">
        {lang === 'en' ? (
          <>
            <h1>Shipping & Taxes</h1>
            <p className="legal-date">Last updated: June 2025</p>

            <section>
              <h2>Two-Stage Billing</h2>
              <p>Incord operates a clear, transparent two-stage billing model. Every order is split into two separate quotes and two separate payments, so you always know exactly what you are paying for and when.</p>
              <ol className="legal-billing-steps">
                <li><strong>Stage 1 — Procurement Quote:</strong> after you submit a product URL, we issue a procurement quote covering the product price, your tax rebate, and a transparent procurement service fee. You pay this first to secure your order.</li>
                <li><strong>Stage 2 — Shipping Quote:</strong> once your goods arrive at our London hub and are packed, we calculate international shipping based on the actual weight and dimensions and issue a separate invoice. No estimated shipping is charged at Stage 1.</li>
              </ol>
              <p>This means you are never asked to pre-pay for shipping on estimated weights — real shipping costs are only billed after your items are safely in hand at our hub.</p>
            </section>

            <section>
              <h2>What Is Included in Each Quote</h2>
              <ul>
                <li><strong>Procurement Quote (Stage 1):</strong> product net price, applicable tax rebate, and our procurement service fee.</li>
                <li><strong>Shipping Quote (Stage 2):</strong> international courier cost, calculated from the actual packed weight and dimensions, plus any applicable export documentation handling.</li>
                <li><strong>Not included:</strong> any import duties or local taxes payable in the destination country — these are charged by your local customs authority and are the buyer's responsibility.</li>
              </ul>
            </section>

            <section>
              <h2>International Shipping Reference Table</h2>
              <p>The table below is a guide for reference only. Final shipping is always quoted separately in Stage 2, based on the actual packed weight and dimensions of your order.</p>
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Zone</th>
                    <th>Regions</th>
                    <th>0–0.5 kg</th>
                    <th>0.5–1 kg</th>
                    <th>1–2 kg</th>
                    <th>2–5 kg</th>
                    <th>5–10 kg</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Zone 1</td>
                    <td>Europe (EU)</td>
                    <td>£18</td>
                    <td>£24</td>
                    <td>£32</td>
                    <td>£48</td>
                    <td>£72</td>
                  </tr>
                  <tr>
                    <td>Zone 2</td>
                    <td>North America</td>
                    <td>£22</td>
                    <td>£29</td>
                    <td>£39</td>
                    <td>£58</td>
                    <td>£88</td>
                  </tr>
                  <tr>
                    <td>Zone 3</td>
                    <td>Asia (incl. Greater China)</td>
                    <td>£20</td>
                    <td>£27</td>
                    <td>£36</td>
                    <td>£55</td>
                    <td>£84</td>
                  </tr>
                  <tr>
                    <td>Zone 4</td>
                    <td>Middle East / Africa</td>
                    <td>£24</td>
                    <td>£32</td>
                    <td>£43</td>
                    <td>£64</td>
                    <td>£98</td>
                  </tr>
                  <tr>
                    <td>Zone 5</td>
                    <td>Oceania / South America</td>
                    <td>£26</td>
                    <td>£35</td>
                    <td>£47</td>
                    <td>£70</td>
                    <td>£106</td>
                  </tr>
                </tbody>
              </table>
              <p className="legal-note">All figures in GBP (£). Weight tiers are based on the actual packed weight including protective packaging. Oversized or high-value items may incur an additional handling quote. The final chargeable amount is always confirmed in your Stage 2 shipping quote.</p>
            </section>

            <section>
              <h2>Taxes & Duty</h2>
              <ul>
                <li><strong>UK VAT / tax rebate:</strong> where eligible, the UK tax rebate is already reflected in your Stage 1 procurement quote.</li>
                <li><strong>Export:</strong> Incord handles UK export documentation and customs clearance for the goods.</li>
                <li><strong>Import duties & local taxes:</strong> any customs duties, import VAT, or local taxes in your destination country are payable by the buyer to your local authorities and are not included in your Incord quotes.</li>
                <li><strong>Compliance:</strong> Incord complies with all applicable UK export regulations. You are responsible for ensuring the goods comply with your destination country's import rules.</li>
              </ul>
            </section>

            <section>
              <h2>Ordering & Payment</h2>
              <ul>
                <li>Stage 1 procurement quote is payable via Stripe (card) or, where offered, Wise transfer.</li>
                <li>Your goods are only procured once your Stage 1 payment is secured.</li>
                <li>Stage 2 shipping is invoiced separately once your goods are packed at our London hub, and must be settled before dispatch.</li>
              </ul>
            </section>

            <section>
              <h2>Contact</h2>
              <p>
                For any questions about shipping or taxes, contact us:<br />
                <strong>Lucky Wheel 11 Ltd.</strong> — Incord (UK)<br />
                Email: <a href="mailto:hello@incord.uk">hello@incord.uk</a><br />
                Company No: 12106600
              </p>
            </section>
          </>
        ) : (
          <>
            <h1>運送與稅務</h1>
            <p className="legal-date">更新日期：2025年6月</p>

            <section>
              <h2>兩段式計費</h2>
              <p>Incord 採用清楚透明的兩段式計費模式。每一筆訂單都會拆成兩份報價與兩次付款，讓您隨時清楚知道付費內容與時間點。</p>
              <ol className="legal-billing-steps">
                <li><strong>第一段 — 採購報價：</strong>您提交商品 URL 後，我們即出具採購報價，內容涵蓋商品價格、退稅優惠與透明的採購服務費。您先支付此費用以鎖定訂單。</li>
                <li><strong>第二段 — 運送報價：</strong>當商品送達我們的倫敦倉庫並完成打包後，我們根據實際重量與材積計算國際運費，另行開立帳單。第一階段不會預收任何估算運費。</li>
              </ol>
              <p>這代表您無需為「估算重量」預先支付運費——真正的運費會在商品安全入庫並完成打包後才收費。</p>
            </section>

            <section>
              <h2>各段報價涵蓋內容</h2>
              <ul>
                <li><strong>採購報價（第一段）：</strong>商品淨價、適用之退稅優惠，以及我們的採購服務費。</li>
                <li><strong>運送報價（第二段）：</strong>國際快遞費用，依實際打包後重量與材積計算，加上適用的出口文件處理費。</li>
                <li><strong>不包含：</strong>目的地國家的進口關稅或當地稅費——由當地海關收取，屬買方責任。</li>
              </ul>
            </section>

            <section>
              <h2>國際運費參考表</h2>
              <p>下表僅供參考。最終運費一律於第二段依實際打包後之重量與材積另行列報。</p>
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>區域</th>
                    <th>地區</th>
                    <th>0–0.5 kg</th>
                    <th>0.5–1 kg</th>
                    <th>1–2 kg</th>
                    <th>2–5 kg</th>
                    <th>5–10 kg</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Zone 1</td>
                    <td>歐洲（歐盟）</td>
                    <td>£18</td>
                    <td>£24</td>
                    <td>£32</td>
                    <td>£48</td>
                    <td>£72</td>
                  </tr>
                  <tr>
                    <td>Zone 2</td>
                    <td>北美</td>
                    <td>£22</td>
                    <td>£29</td>
                    <td>£39</td>
                    <td>£58</td>
                    <td>£88</td>
                  </tr>
                  <tr>
                    <td>Zone 3</td>
                    <td>亞洲（含大中華區）</td>
                    <td>£20</td>
                    <td>£27</td>
                    <td>£36</td>
                    <td>£55</td>
                    <td>£84</td>
                  </tr>
                  <tr>
                    <td>Zone 4</td>
                    <td>中東／非洲</td>
                    <td>£24</td>
                    <td>£32</td>
                    <td>£43</td>
                    <td>£64</td>
                    <td>£98</td>
                  </tr>
                  <tr>
                    <td>Zone 5</td>
                    <td>大洋洲／南美洲</td>
                    <td>£26</td>
                    <td>£35</td>
                    <td>£47</td>
                    <td>£70</td>
                    <td>£106</td>
                  </tr>
                </tbody>
              </table>
              <p className="legal-note">所有金額為英鎊（£）。重量等級以含保護包裝之實際打包重量為準。超大型或高價值商品可能另收處理費。最終應付金額一律以您的第二段運送報價為準。</p>
            </section>

            <section>
              <h2>稅務與關稅</h2>
              <ul>
                <li><strong>英國 VAT／退稅：</strong>符合資格者，英國退稅優惠已反映於您的第一段採購報價中。</li>
                <li><strong>出口：</strong>Incord 負責處理商品的英國出口文件與報關。</li>
                <li><strong>進口關稅與當地稅費：</strong>目的地國家的任何關稅、進口 VAT 或當地稅費，須由買方向當地主管機關支付，不包含在 Incord 報價內。</li>
                <li><strong>合規：</strong>Incord 遵守所有適用的英國出口法規。買方有責任確保商品符合目的地國家的進口規範。</li>
              </ul>
            </section>

            <section>
              <h2>訂購與付款</h2>
              <ul>
                <li>第一段採購報價可透過 Stripe（信用卡）或（如提供）Wise 轉帳支付。</li>
                <li>只有在您的第一段付款確認後，我們才會開始採購商品。</li>
                <li>第二段運費於商品在倫敦倉庫完成打包後另行開立帳單，並須在出貨前結清。</li>
              </ul>
            </section>

            <section>
              <h2>聯絡資訊</h2>
              <p>
                若有任何運送或稅務問題，請聯絡我們：<br />
                <strong>Lucky Wheel 11 Ltd.</strong> — Incord (UK)<br />
                電子郵件：<a href="mailto:hello@incord.uk">hello@incord.uk</a><br />
                公司註冊號：12106600
              </p>
            </section>
          </>
        )}
      </div>

      <footer className="legal-footer">
        <div className="container">
          <Link to="/" style={{ color: 'var(--accent-gold)', textDecoration: 'none' }}>← {t('common.home')}</Link>
        </div>
      </footer>
    </main>
  );
};

export default ShippingPolicy;
